import React, { useState } from 'react'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import LoadingBackdrop from '../../../Common/Loading/LoadingBackdrop'
import { IconButton, Tooltip } from '@mui/material'
import useConfirmationAlert from '../../../../hooks/componentHooks/useConfirmationAlert'
import { useAppDispatch, useAppSelector } from '../../../../redux/store'
import { clearSelectedEvent } from '../../../../redux/event/manageEvents/slice'
import { fetchEventDetails, updateEvent } from '../../../../redux/event/manageEvents/actions'
import { selectUserTeamMemberInfo } from '../../../../redux/auth/selector'
import showToast from '../../../Common/SweetAlert/ToastAlert'

interface EventArchiveModalProps {
  id: string
}

const EventArchiveModal: React.FC<EventArchiveModalProps> = ({ id }) => {
  const dispatch = useAppDispatch()
  const { ConfirmationAlert, openConfirmationAlert } = useConfirmationAlert()
  const teamMemberDetails = useAppSelector(selectUserTeamMemberInfo)
  const [isLoading, setIsLoading] = useState(false)

  const archiveEvent = async (eventId: number) => {
    if (!teamMemberDetails) {
      showToast('error', 'User information not available')
      return
    }

    try {
      await dispatch(
        updateEvent({
          id: eventId,
          isArchived: true,
          updatedBy: {
            email: teamMemberDetails.email,
            workdayId: teamMemberDetails.workdayId,
          },
        }),
      ).unwrap()
      showToast('success', 'Event archived successfully!')
    } catch (error) {
      console.error('Error updating event archive status:', error)
      showToast('error', 'Error updating event archive status')
    }
  }

  const handleArchiveEvent = async () => {
    setIsLoading(true)
    try {
      const currentEventDetails = await dispatch(fetchEventDetails(id)).unwrap()
      if (!currentEventDetails?.id) {
        throw new Error('Failed to fetch event details')
      }

      openConfirmationAlert({
        title: 'Archive event',
        text: 'Are you sure you want to archive this event?',
        onConfirm: async () => {
          await archiveEvent(currentEventDetails.id)
          dispatch(clearSelectedEvent())
        },
        onCancel: () => dispatch(clearSelectedEvent()),
      })
    } catch (error) {
      console.error('Error fetching event details:', error)
      showToast('error', 'Error fetching event details')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <Tooltip title='Archive' arrow>
        <IconButton
          id='archive-event-button'
          aria-label='archive'
          color='error'
          size='small'
          onClick={handleArchiveEvent}
        >
          <DeleteIcon />
        </IconButton>
      </Tooltip>
      {isLoading && <LoadingBackdrop message='Processing...' />}
      {ConfirmationAlert}
    </>
  )
}

export default EventArchiveModal
